import { createFileRoute, redirect, Outlet } from '@tanstack/react-router';
import { useEffect } from 'react';

import { CAPABILITY_KEYS, isSimplyListingsPackage } from '@brightlocal/shared';

import { revalidateAuthMeOrRedirect } from 'shared/hooks';
import { isCapabilityEnabled, revalidateCustomerCapabilities } from 'shared/modules/capabilities';
import { routes } from 'shared/routes';

import { usePostLoginNavigation } from 'features/auth/shared';
import { GET_STARTED_STEPS, useGetStartedStore } from 'features/get-started/hooks';
import { getExpectedBasicFlowOnboardingPath } from 'features/get-started/utils';

export const Route = createFileRoute('/_authenticated/get-started/_get-started')({
  // Redirect users who have completed onboarding before rendering anything,
  // preventing a flash of get-started content for users that shouldn't be here.
  // Revalidate auth/me and the customer capabilities first so the plan branches below use fresh
  // data (the plan can change out-of-band mid-onboarding); the `_authenticated` beforeLoad has
  // already seeded both. The prompts step follows the AI Visibility capability.
  beforeLoad: async ({ context, location }) => {
    const [authUser, customerCapabilities] = await Promise.all([
      revalidateAuthMeOrRedirect(context.queryClient, location.href),
      revalidateCustomerCapabilities({ queryClient: context.queryClient, attemptedHref: location.href })
    ]);

    const { completedSteps } = useGetStartedStore.getState();
    const isBasicFlow = !isSimplyListingsPackage(authUser.subscription.packageId);
    const hasLocation = completedSteps.includes(GET_STARTED_STEPS.FIND_LOCATION);
    const hasKeywords = completedSteps.includes(GET_STARTED_STEPS.ADD_KEYWORDS);
    const hasPrompts = completedSteps.includes(GET_STARTED_STEPS.ADD_PROMPTS);
    const hasGettingInsights = completedSteps.includes(GET_STARTED_STEPS.GETTING_INSIGHTS);

    // Finished onboarding — never re-enter get-started, even if auth/me still reports
    // `activeLocations === 0` (stale cache) or Back lands on an old find-location entry.
    if (hasGettingInsights) {
      throw redirect({ to: routes.locations(), replace: true });
    }

    if (!authUser.customer.name) {
      return;
    }

    if (authUser.subscription.activeLocations === 0) {
      return;
    }

    // Mid basic flow (has a location, insights not done): only the current step is allowed.
    // Wrong URLs (e.g. browser Back to find-location) are replaced with the expected step.
    if (isBasicFlow && hasLocation) {
      const expectedPath = getExpectedBasicFlowOnboardingPath({
        hasKeywords,
        hasPrompts,
        canShowPrompts: isCapabilityEnabled({
          capabilities: customerCapabilities,
          key: CAPABILITY_KEYS.AI_VISIBILITY
        }),
        currentPath: location.pathname
      });

      // Already on the incomplete Add Prompts step — stay put.
      if (expectedPath === null) {
        return;
      }

      if (!location.pathname.startsWith(expectedPath)) {
        throw redirect({ to: expectedPath, replace: true });
      }

      return;
    }

    throw redirect({ to: routes.locations(), replace: true });
  },
  component: GetStartedLayout
});

function GetStartedLayout() {
  const { navigatePostLogin } = usePostLoginNavigation();

  // Bounce users to the correct onboarding step on reload or direct URL visits.
  // The `_authenticated` route's beforeLoad guarantees auth/me is in cache by now.
  useEffect(() => {
    navigatePostLogin();
  }, [navigatePostLogin]);

  return <Outlet />;
}
